import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../authentication/hooks/useAuth';
import { useLocation } from '../../../hooks/weather/useLocation';
import { useWeather } from '../../../hooks/weather/useWeather';
import WeatherBackground from '../../../components/Weather/WeatherBackground';
import WeatherInfo from '../../../components/Weather/WeatherInfo';
import { ModernDateTime } from './ModernDateTime';
import { WeatherCondition } from '../../../api/weather/types';
import { usePerformanceMonitor } from '../../../hooks/weather/usePerformanceMonitor';
import { logComponentState } from '../../../utils/debug';
import styles from '../styles/home.module.scss';

interface WelcomeHeaderState {
  weather: {
    condition: WeatherCondition;
    temperature?: number;
    description?: string;
    location?: string;
    lastUpdated?: Date;
  };
  dateTime: {
    current: Date;
    timezone: string;
  };
  loading: {
    location: boolean;
    weather: boolean;
  };
  error: {
    location?: string;
    weather?: string;
  };
}

export const WelcomeHeader = React.memo(() => {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Performance monitoring
  usePerformanceMonitor(
    { componentName: 'WelcomeHeader', trackProps: true },
    [user?.data?.firstname, user?.data?.username]
  );
  
  // Weather and location hooks with enhanced error handling
  const { 
    location, 
    isLoading: isLocationLoading, 
    error: locationError,
    isFallback: isLocationFallback,
    errorSeverity: locationErrorSeverity,
    hasRecoverableError: hasRecoverableLocationError
  } = useLocation();
  
  const { 
    weather, 
    isLoading: isWeatherLoading, 
    error: weatherError,
    isFallback: isWeatherFallback,
    fallbackReason: weatherFallbackReason,
    errorSeverity: weatherErrorSeverity,
    hasRecoverableError: hasRecoverableWeatherError
  } = useWeather({ 
    location, 
    enabled: !!location 
  });

  // Debug logging
  useEffect(() => {
    logComponentState('WelcomeHeader', {
      user: user?.data?.username,
      location: !!location,
      weather: !!weather,
      isLocationLoading,
      isWeatherLoading,
      locationError,
      weatherError
    });
  }, [user, location, weather, isLocationLoading, isWeatherLoading, locationError, weatherError]);

  // Consolidated state management for the enhanced header
  const state = useMemo<WelcomeHeaderState>(() => ({
    weather: {
      condition: weather?.condition || 'default',
      temperature: weather?.temperature,
      description: weather?.description,
      location: weather ? `${weather.location.city}, ${weather.location.country}` : undefined,
      lastUpdated: weather?.timestamp,
    },
    dateTime: {
      current: currentTime,
      timezone: location?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    loading: {
      location: isLocationLoading,
      weather: isWeatherLoading,
    },
    error: {
      location: locationError || undefined,
      weather: weatherError || undefined,
    },
  }), [
    weather, 
    currentTime, 
    location, 
    isLocationLoading, 
    isWeatherLoading, 
    locationError, 
    weatherError
  ]);

  // Update time every second - memoized to prevent unnecessary re-creation
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Memoized formatting functions to prevent recreation
  const formatDate = useCallback((date: Date, timezone: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone
    };
    return date.toLocaleDateString('es-ES', options);
  }, []);

  const formatTime = useCallback((date: Date, timezone: string) => {
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone
    });
  }, []);

  const getGreeting = useCallback((time: Date) => {
    const hour = time.getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  }, []);

  // Memoized computed values for performance
  const computedValues = useMemo(() => {
    const userName = user?.data?.firstname || user?.data?.username || 'Usuario';

    // Enhanced error and fallback state management
    const hasLocationError = !!state.error.location && !isLocationFallback;
    const hasWeatherError = !!state.error.weather && !isWeatherFallback;
    const hasCriticalError = (locationErrorSeverity === 'high' && hasLocationError) || 
                            (weatherErrorSeverity === 'high' && hasWeatherError);
    
    // Show weather features with graceful degradation
    const showWeatherFeatures = !hasCriticalError && (!!location || isLocationFallback);
    const hasWeatherData = !!weather && !state.loading.weather;
    const isAnyLoading = state.loading.location || state.loading.weather;
    
    // Determine if we should show recovery options
    const canRecover = hasRecoverableLocationError || hasRecoverableWeatherError;

    return {
      userName,
      showWeatherFeatures,
      hasWeatherData,
      isAnyLoading,
      hasLocationError,
      hasWeatherError,
      hasCriticalError,
      canRecover,
      isLocationFallback,
      isWeatherFallback,
      locationErrorSeverity,
      weatherErrorSeverity,
      greeting: getGreeting(currentTime),
      formattedDate: formatDate(currentTime, state.dateTime.timezone),
      formattedTime: formatTime(currentTime, state.dateTime.timezone)
    };
  }, [
    currentTime, user, state, weather, location,
    isLocationFallback, isWeatherFallback,
    locationErrorSeverity, weatherErrorSeverity,
    hasRecoverableLocationError, hasRecoverableWeatherError,
    getGreeting, formatDate, formatTime
  ]);

  return (
    <div className={styles.welcomeHeader}>
      {/* Weather Background - always render for consistent layout */}
      <WeatherBackground
        weatherCondition={state.weather.condition}
        temperature={state.weather.temperature}
        isLoading={computedValues.isAnyLoading}
        hasError={computedValues.hasWeatherError}
        isFallback={computedValues.isWeatherFallback}
        errorSeverity={computedValues.weatherErrorSeverity}
      />
      
      <div className={styles.welcomeHeader__content}>
        <div className={styles.welcomeHeader__greeting}>
          <h1 className={styles.welcomeHeader__title}>
            {computedValues.greeting}, {computedValues.userName}!
          </h1>
          
          {/* Enhanced DateTime with graceful fallback */}
          <div className={styles.welcomeHeader__dateTimeContainer}>
            {computedValues.showWeatherFeatures ? (
              <div className={styles.welcomeHeader__modernDateTime}>
                <ModernDateTime
                  timezone={state.dateTime.timezone}
                  weatherCondition={state.weather.condition}
                />
              </div>
            ) : (
              <div className={styles.welcomeHeader__fallbackDateTime}>
                <p className={styles.welcomeHeader__subtitle}>
                  {computedValues.formattedDate}
                </p>
                <div className={styles.welcomeHeader__time}>
                  <span className={styles.welcomeHeader__timeText}>
                    {computedValues.formattedTime}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.welcomeHeader__rightSection}>
          {/* Weather Info - show when available or loading */}
          {(computedValues.showWeatherFeatures || state.loading.weather) && (
            <div className={styles.welcomeHeader__weatherInfo}>
              <WeatherInfo
                temperature={state.weather.temperature}
                condition={state.weather.condition}
                description={state.weather.description}
                location={state.weather.location}
                isVisible={computedValues.hasWeatherData}
                isLoading={state.loading.weather}
                isFallback={computedValues.isWeatherFallback}
                fallbackReason={weatherFallbackReason}
                hasError={computedValues.hasWeatherError}
                errorSeverity={computedValues.weatherErrorSeverity}
              />
            </div>
          )}

          {/* Enhanced error state indicator */}
          {(computedValues.hasLocationError || computedValues.hasWeatherError) && !computedValues.isAnyLoading && (
            <div className={`${styles.welcomeHeader__errorIndicator} ${styles[`errorIndicator--${computedValues.locationErrorSeverity || computedValues.weatherErrorSeverity}`]}`}>
              <div className={styles.welcomeHeader__errorText}>
                {computedValues.hasLocationError ? 
                  (computedValues.locationErrorSeverity === 'high' ? 'Ubicación no disponible' : 'Ubicación limitada') : 
                  (computedValues.weatherErrorSeverity === 'high' ? 'Clima no disponible' : 'Clima limitado')
                }
              </div>
              
              {/* Recovery action button for recoverable errors */}
              {computedValues.canRecover && (
                <button 
                  className={styles.welcomeHeader__retryButton}
                  onClick={() => {
                    if (hasRecoverableLocationError) {
                      // Retry location detection
                      console.log('Retrying location detection...');
                    }
                    if (hasRecoverableWeatherError) {
                      // Retry weather fetch
                      console.log('Retrying weather fetch...');
                    }
                  }}
                  title="Intentar de nuevo"
                >
                  🔄
                </button>
              )}
            </div>
          )}

          {/* Fallback state indicator */}
          {(computedValues.isLocationFallback || computedValues.isWeatherFallback) && !computedValues.isAnyLoading && (
            <div className={styles.welcomeHeader__fallbackIndicator}>
              <div className={styles.welcomeHeader__fallbackText}>
                {computedValues.isLocationFallback && computedValues.isWeatherFallback ? 
                  'Datos estimados' :
                  computedValues.isLocationFallback ? 
                    'Ubicación estimada' : 
                    'Clima estimado'
                }
              </div>
              <div className={styles.welcomeHeader__fallbackIcon} title="Usando datos por defecto">
                ℹ️
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Loading state indicator */}
      {computedValues.isAnyLoading && (
        <div className={styles.welcomeHeader__loadingIndicator}>
          <div className={styles.welcomeHeader__loadingSpinner}></div>
          <div className={styles.welcomeHeader__loadingText}>
            {state.loading.location ? 'Detectando ubicación...' : 'Obteniendo clima...'}
          </div>
        </div>
      )}
    </div>
  );
}); 